/*
This piece of code is used for generating video previews. It used to be under dashboard-operations.js for generating
previews for video tiles on dashboard, but now that we need to generate previews for blueprints, it has been
move to its own module.
*/
import type Immutable from 'immutable';
import type { OpaqueVideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { uploadVideoPosterBlob } from 'js/src/data/app/upload-video-poster-operations';
import { storeLuminaryThumbnail } from 'js/src/data/thumbnail-previews';
import CancellablePromiseChain from 'js/src/libs/cancellable-promise-chain';
import type { AppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

export const luminaryPromiseChain = new CancellablePromiseChain();

export const loadLuminaryPreviewsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        {
            newVideoDataList,
            newIdList,
            videoIdsForPosterUpload,
        }: {
            newVideoDataList: OpaqueVideoData[];
            newIdList: Immutable.List<number>;
            videoIdsForPosterUpload?: Immutable.List<number | undefined>;
        },
    ) => {
        if (newVideoDataList.length !== newIdList.size) {
            throw Error(
                `The size of the list of video data (${newVideoDataList.length}) is not the same as the list of ids (${newIdList.size})`,
            );
        }

        // Generate placeholder images for videos that have not been rendered
        newVideoDataList.forEach((videoData, index) => {
            _loadLuminaryFor(
                luminary,
                dispatch,
                videoData,
                newIdList.get(index)!,
                luminaryPromiseChain,
                videoIdsForPosterUpload?.get(index),
            );
        });
    },
);

const THUMBNAIL_LOADING_TIMEOUT = 3000; // 3 seconds
function _loadLuminaryFor(
    luminary: Luminary,
    dispatch: AppDispatch,
    videoData: OpaqueVideoData,
    id: number,
    luminaryPromiseChain: CancellablePromiseChain,
    videoIdForPosterUpload?: number,
) {
    luminaryPromiseChain.then(() => {
        return Promise.race([
            (async () => {
                await luminary.clear();

                // Load the video data into luminary and wait for it to finish loading
                const slicedVideoData = VideoData.sliceVideoData(videoData, {
                    start: 0,
                    end: 0,
                    preserveTransitions: false,
                });
                luminary.load(slicedVideoData.videoData);
                if (luminary.scenes.length === 0) {
                    return;
                }
                await luminary.onAssetsDoneTryingToLoad();

                // Create the canvas to render to
                const canvas = document.createElement('canvas');
                const { width, height } = VideoData.getResolution(
                    luminary.resolutionValue,
                    luminary.aspectRatio,
                );
                canvas.width = width;
                canvas.height = height;

                // Render the first slide to the canvas and store it
                await luminary.scenes[0].renderPreviewToCanvas(canvas);
                dispatch(storeLuminaryThumbnail({ id, image: canvas }));

                // Upload the thumbnail as the video's poster if a video ID is provided
                if (videoIdForPosterUpload) {
                    const fileName = `${uuid()}.jpg`;
                    const fileType = 'image/jpeg';
                    const quality = 0.95;
                    canvas.toBlob(
                        (blob) =>
                            uploadVideoPosterBlob(blob, fileName, fileType, videoIdForPosterUpload),
                        fileType,
                        quality,
                    );
                }
            })(),

            new Promise((_resolve, reject) =>
                setTimeout(
                    () => reject(new Error('Thumbnail took too long to load, aborting')),
                    THUMBNAIL_LOADING_TIMEOUT,
                ),
            ),
        ]).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn(e);
        });
    });
}
